import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ShowChart as ShowChartIcon } from '@mui/icons-material';
import {
  DEPLOYMENT_COUNT,
  DeploymentSpreadCell,
  MAX_DAILY_OCCURRENCE,
  RANKED_SPECIES_COUNT,
  SpeciesCatalogEntry,
  TIME_SLOTS,
  slotEnd,
} from './data/catalogData';
import { SPREAD_CLASSES } from './SpeciesSpreadClassCard';

export const CARD_UI = {
  accent: '#10B981',
  peak: '#22D3EE',
  text: '#F5F5F5',
  textSecondary: '#9CA3AF',
  textMuted: '#6B7280',
  panelBorder: '#1F2A24',
  panelBg: 'rgba(255, 255, 255, 0.015)',
  track: 'rgba(255, 255, 255, 0.06)',
  emptyCell: 'rgba(255, 255, 255, 0.05)',
};

const SECTION_LABEL_SX = {
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.9px',
  color: CARD_UI.textMuted,
  textTransform: 'uppercase' as const,
  lineHeight: 1.2,
};

const PANEL_SX = {
  border: `1px solid ${CARD_UI.panelBorder}`,
  borderRadius: '10px',
  bgcolor: CARD_UI.panelBg,
  p: '14px',
};

export const spreadClassMeta = (id: SpeciesCatalogEntry['spreadClass']) =>
  SPREAD_CLASSES.find((c) => c.id === id) ?? SPREAD_CLASSES[SPREAD_CLASSES.length - 1];

export const formatNumber = (n: number) => n.toLocaleString('en-US');

export const formatPeak = (species: Pick<SpeciesCatalogEntry, 'peakTime' | 'peakBand'>): string => {
  if (!species.peakTime) return '—';
  return species.peakBand ? `${species.peakTime} · ${species.peakBand}` : species.peakTime;
};

/**
 * Opacity for a measured deployment cell on a log scale, so one very busy deployment
 * doesn't flatten every other cell to the same faint shade.
 */
export const spreadCellOpacity = (value: number, max: number): number => {
  if (max <= 0) return 0.3;
  const t = Math.log1p(value) / Math.log1p(max);
  return 0.3 + 0.7 * t;
};

export const describeSpreadCell = (cell: DeploymentSpreadCell): string => {
  if (cell.status === 'measured') return `${cell.deployment} · ${formatNumber(cell.value as number)} / day`;
  if (cell.status === 'no-activity-data') return `${cell.deployment} · no activity data`;
  return `${cell.deployment} · not detected`;
};

/** Pill badge used for spread class and taxon. */
export const Badge: React.FC<{ label: string; color?: string }> = ({ label, color }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      height: '24px',
      px: 1.25,
      borderRadius: '999px',
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '0.8px',
      textTransform: 'uppercase',
      color: color ?? CARD_UI.textSecondary,
      bgcolor: color ? `${color}1A` : 'rgba(255, 255, 255, 0.04)',
      border: `1px solid ${color ? `${color}66` : 'rgba(255, 255, 255, 0.14)'}`,
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
);

const MetricRow: React.FC<{ label: string; value: string; fraction?: number }> = ({
  label,
  value,
  fraction,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: '20px' }}>
    <Typography sx={{ ...SECTION_LABEL_SX, width: '124px', flexShrink: 0, whiteSpace: 'nowrap' }}>{label}</Typography>
    <Box sx={{ flex: 1, height: '5px', borderRadius: '999px', bgcolor: fraction === undefined ? 'transparent' : CARD_UI.track, overflow: 'hidden' }}>
      {fraction !== undefined && fraction > 0 && (
        <Box
          sx={{
            height: '100%',
            width: `${Math.max(2, Math.min(100, fraction * 100))}%`,
            bgcolor: CARD_UI.accent,
            borderRadius: '999px',
          }}
        />
      )}
    </Box>
    <Typography
      sx={{
        fontSize: '13px',
        fontWeight: 700,
        color: CARD_UI.text,
        minWidth: '52px',
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const AXIS_TICKS = ['00', '06', '12', '18', '24'];

/** 48 half-hour bars; the summary's peak slot is drawn in the highlight colour. */
const DailyPatternBars: React.FC<{
  species: SpeciesCatalogEntry;
  onHover: (slotIndex: number | null) => void;
  hovered: number | null;
}> = ({ species, onHover, hovered }) => {
  const peakIndex = species.peakTime ? TIME_SLOTS.indexOf(species.peakTime) : -1;
  return (
    <Box>
      <Box
        role="img"
        aria-label={`Daily pattern for ${species.name}, peak at ${species.peakTime ?? 'unknown'}`}
        onMouseLeave={() => onHover(null)}
        sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '64px' }}
      >
        {species.pattern.map((v, i) => {
          const isPeak = i === peakIndex;
          const height = species.patternMax > 0 && v > 0 ? Math.max(3, (v / species.patternMax) * 100) : 0;
          return (
            <Box
              key={TIME_SLOTS[i]}
              onMouseEnter={() => onHover(i)}
              sx={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end', cursor: 'default' }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: `${height}%`,
                  bgcolor: isPeak ? CARD_UI.peak : CARD_UI.accent,
                  borderRadius: '2px 2px 0 0',
                  opacity: hovered === null || hovered === i ? 1 : 0.55,
                  transition: 'opacity 0.12s ease',
                }}
              />
            </Box>
          );
        })}
      </Box>
      <Box sx={{ position: 'relative', height: '14px', mt: 1 }}>
        {AXIS_TICKS.map((tick, i) => (
          <Typography
            key={tick}
            sx={{
              position: 'absolute',
              left: `${(i / (AXIS_TICKS.length - 1)) * 100}%`,
              transform: i === 0 ? 'none' : i === AXIS_TICKS.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
              fontSize: '10px',
              color: CARD_UI.textMuted,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
            }}
          >
            {tick}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

/** One cell per deployment: filled = measured (brighter = higher), outlined = detected without activity data. */
export const DeploymentSpreadStrip: React.FC<{
  cells: DeploymentSpreadCell[];
  onHover?: (index: number | null) => void;
  hovered?: number | null;
  height?: number;
}> = ({ cells, onHover, hovered = null, height = 16 }) => {
  const max = Math.max(0, ...cells.map((c) => c.value ?? 0));
  return (
    <Box onMouseLeave={() => onHover?.(null)} sx={{ display: 'flex', gap: '3px' }}>
      {cells.map((cell, i) => (
        <Box
          key={cell.deployment}
          onMouseEnter={() => onHover?.(i)}
          title={onHover ? undefined : describeSpreadCell(cell)}
          sx={{
            flex: 1,
            height: `${height}px`,
            borderRadius: '2px',
            boxSizing: 'border-box',
            outline: hovered === i ? `1px solid ${CARD_UI.text}` : 'none',
            outlineOffset: '1px',
            ...(cell.status === 'measured'
              ? { bgcolor: CARD_UI.accent, opacity: spreadCellOpacity(cell.value as number, max) }
              : cell.status === 'no-activity-data'
                ? { border: `1px solid ${CARD_UI.accent}99` }
                : { bgcolor: CARD_UI.emptyCell }),
          }}
        />
      ))}
    </Box>
  );
};

interface SpeciesCardProps {
  species: SpeciesCatalogEntry;
  onOpenDetails?: (species: SpeciesCatalogEntry) => void;
}

const SpeciesCard: React.FC<SpeciesCardProps> = ({ species, onOpenDetails }) => {
  const [hoveredSlot, setHoveredSlot] = useState<number | null>(null);
  const [hoveredDeployment, setHoveredDeployment] = useState<number | null>(null);
  const spreadMeta = spreadClassMeta(species.spreadClass);
  const hasPattern = species.patternMax > 0;

  const patternReadout =
    hoveredSlot !== null
      ? `${TIME_SLOTS[hoveredSlot]}–${slotEnd(TIME_SLOTS[hoveredSlot])} · ${formatNumber(species.pattern[hoveredSlot])}`
      : `${species.deploymentsWithActivityData} deployment${species.deploymentsWithActivityData === 1 ? '' : 's'}`;

  const spreadReadout =
    hoveredDeployment !== null ? describeSpreadCell(species.spread[hoveredDeployment]) : 'Brighter = higher occurrence';

  return (
    <Box
      sx={{
        background: 'linear-gradient(180deg, rgba(18, 24, 21, 0.85) 0%, rgba(10, 14, 12, 0.95) 100%)',
        border: '1px solid #1E2E25',
        borderRadius: '14px',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
        minWidth: 0,
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          borderColor: 'rgba(16, 185, 129, 0.4)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
        },
      }}
    >
      {/* Spread class & taxon */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Badge label={spreadMeta.name} color={spreadMeta.color} />
        <Badge label={species.type} />
      </Box>

      {/* Name & rank */}
      <Box>
        <Typography
          title={species.name}
          sx={{
            fontSize: '19px',
            fontWeight: 700,
            color: '#FFFFFF',
            lineHeight: 1.25,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {species.name}
        </Typography>
        <Typography sx={{ ...SECTION_LABEL_SX, color: CARD_UI.textSecondary, mt: 0.75 }}>
          {species.rank !== null
            ? `Daily occurrence rank ${species.rank} of ${RANKED_SPECIES_COUNT}`
            : 'No daily occurrence data'}
        </Typography>
      </Box>

      {/* Daily occurrence, deployments, peak time */}
      <Box sx={{ ...PANEL_SX, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <MetricRow
          label="Daily occurrence"
          value={species.dailyOccurrence !== null ? formatNumber(species.dailyOccurrence) : '—'}
          fraction={(species.dailyOccurrence ?? 0) / MAX_DAILY_OCCURRENCE}
        />
        <MetricRow
          label="Deployments"
          value={`${species.deploymentsDetected}/${DEPLOYMENT_COUNT}`}
          fraction={species.deploymentsDetected / DEPLOYMENT_COUNT}
        />
        <MetricRow
          label="Peak time"
          value={formatPeak(species)}
        />
      </Box>

      {/* Daily pattern */}
      <Box sx={PANEL_SX}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
          <Typography sx={SECTION_LABEL_SX}>Daily pattern</Typography>
          {hasPattern && (
            <Typography
              sx={{
                ...SECTION_LABEL_SX,
                color: hoveredSlot !== null ? CARD_UI.text : CARD_UI.textMuted,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {patternReadout}
            </Typography>
          )}
        </Box>
        {hasPattern ? (
          <DailyPatternBars species={species} hovered={hoveredSlot} onHover={setHoveredSlot} />
        ) : (
          <Box
            sx={{
              height: '86px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              border: `1px dashed ${CARD_UI.panelBorder}`,
              borderRadius: '8px',
              px: 2,
            }}
          >
            <Typography sx={{ fontSize: '11px', color: CARD_UI.textMuted, lineHeight: 1.4 }}>
              Detected only in deployments without activity data, so no daily pattern is available.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Deployment spread */}
      <Box sx={PANEL_SX}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25, gap: 1 }}>
          <Typography sx={{ ...SECTION_LABEL_SX, flexShrink: 0 }}>Deployment spread</Typography>
          <Typography
            sx={{
              ...SECTION_LABEL_SX,
              color: hoveredDeployment !== null ? CARD_UI.text : CARD_UI.textMuted,
              textAlign: 'right',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {spreadReadout}
          </Typography>
        </Box>
        <DeploymentSpreadStrip cells={species.spread} hovered={hoveredDeployment} onHover={setHoveredDeployment} />
      </Box>

      <Button
        fullWidth
        variant="outlined"
        startIcon={<ShowChartIcon sx={{ fontSize: 16 }} />}
        onClick={() => onOpenDetails?.(species)}
        sx={{
          mt: 'auto',
          py: 1.25,
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '1px',
          textTransform: 'uppercase',
          color: CARD_UI.accent,
          borderColor: 'rgba(16, 185, 129, 0.35)',
          bgcolor: 'rgba(16, 185, 129, 0.08)',
          borderRadius: '10px',
          '&:hover': {
            borderColor: CARD_UI.accent,
            bgcolor: 'rgba(16, 185, 129, 0.16)',
          },
        }}
      >
        Daily pattern & spread
      </Button>
    </Box>
  );
};

export default React.memo(SpeciesCard);
